import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { icons } from '../../components/ui/Icons';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Badge } from '../../components/ui/Badge';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/Card';
import { Table } from '../../components/ui/Table';
import { Select } from '../../components/ui/Select';
import { Alert } from '../../components/ui/Alert';
import { EmptyState } from '../../components/ui/EmptyState';
import { ErrorState } from '../../components/ui/ErrorState';
import { SkeletonTable } from '../../components/ui/Skeleton';
import { Breadcrumb } from '../../components/ui/Breadcrumb';
import { AuditLog } from '../../types/auth';
import { requestJson, getErrorMessage, isAbortError, formatDate } from '../../lib/adminApi';

const AUDIT_PAGE_SIZE = 50;

type AuditTimeRange = 'all' | '1h' | '24h' | '7d' | '30d';

interface AuditFilters {
  search: string;
  userId: string;
  action: string;
  resource: string;
  details: string;
  ipAddress: string;
  timeRange: AuditTimeRange;
  from: string;
  to: string;
}

const AUDIT_TIME_OPTIONS: Array<{ value: AuditTimeRange; label: string }> = [
  { value: 'all', label: 'Gesamter Zeitraum' },
  { value: '1h', label: 'Letzte Stunde' },
  { value: '24h', label: 'Letzte 24 Stunden' },
  { value: '7d', label: 'Letzte 7 Tage' },
  { value: '30d', label: 'Letzte 30 Tage' },
];

function parseAuditPayload(value: string | null): unknown {
  if (!value) {
    return null;
  }
  try {
    return JSON.parse(value);
  } catch {
    return value;
  }
}

function formatAuditPayload(value: unknown): string {
  if (value === null || value === undefined) {
    return '-';
  }
  if (typeof value === 'string') {
    return value;
  }
  try {
    return JSON.stringify(value, null, 2);
  } catch {
    return String(value);
  }
}

function AuditDetails({ log }: { log: AuditLog }) {
  const hasDetails = log.oldData !== null || log.newData !== null;

  if (!hasDetails) {
    return <span className="text-neutral-400">-</span>;
  }

  return (
    <details className="group min-w-[180px] max-w-xs">
      <summary className="flex cursor-pointer items-center gap-1 text-xs font-medium text-primary-600 dark:text-primary-400">
        <icons.FileText className="h-3.5 w-3.5" />
        Details
      </summary>
      <div className="mt-2 space-y-3 rounded-lg border border-neutral-200 dark:border-neutral-700 bg-neutral-50 dark:bg-neutral-900/50 p-2">
        {log.oldData !== null && (
          <div>
            <p className="mb-1 text-[10px] uppercase tracking-wide text-neutral-500 dark:text-neutral-400">Alt</p>
            <pre className="max-h-28 overflow-auto whitespace-pre-wrap break-words font-mono text-[11px] text-neutral-700 dark:text-neutral-300">
              {formatAuditPayload(parseAuditPayload(log.oldData))}
            </pre>
          </div>
        )}
        {log.newData !== null && (
          <div>
            <p className="mb-1 text-[10px] uppercase tracking-wide text-neutral-500 dark:text-neutral-400">Neu</p>
            <pre className="max-h-28 overflow-auto whitespace-pre-wrap break-words font-mono text-[11px] text-neutral-700 dark:text-neutral-300">
              {formatAuditPayload(parseAuditPayload(log.newData))}
            </pre>
          </div>
        )}
      </div>
    </details>
  );
}

function normalizeAuditLog(value: unknown): AuditLog {
  const data = value as Partial<AuditLog>;
  return {
    id: data.id || '',
    userId: data.userId || 'Unbekannt',
    userName: data.userName,
    action: data.action || 'UNBEKANNT',
    resource: data.resource || 'Unbekannt',
    resourceId: data.resourceId ?? null,
    oldData: data.oldData ?? null,
    newData: data.newData ?? null,
    ipAddress: data.ipAddress ?? null,
    userAgent: data.userAgent ?? null,
    createdAt: data.createdAt || '',
  };
}

export function AuditLogPage() {
  const [auditLogs, setAuditLogs] = useState<AuditLog[]>([]);
  const [auditLoading, setAuditLoading] = useState(false);
  const [auditError, setAuditError] = useState<string | null>(null);
  const [auditPage, setAuditPage] = useState(1);
  const [auditFilters, setAuditFilters] = useState<AuditFilters>({
    search: '',
    userId: '',
    action: '',
    resource: '',
    details: '',
    ipAddress: '',
    timeRange: 'all',
    from: '',
    to: '',
  });

  const auditRequestRef = useRef<AbortController | null>(null);

  const fetchAuditLogs = useCallback(async (signal?: AbortSignal) => {
    setAuditLoading(true);
    setAuditError(null);
    const params = new URLSearchParams({
      limit: String(AUDIT_PAGE_SIZE),
      offset: String((auditPage - 1) * AUDIT_PAGE_SIZE),
    });
    if (auditFilters.action.trim()) {
      params.append('action', auditFilters.action.trim());
    }
    if (auditFilters.userId.trim()) {
      params.append('userId', auditFilters.userId.trim());
    }
    if (auditFilters.search.trim()) {
      params.append('search', auditFilters.search.trim());
    }
    if (auditFilters.resource.trim()) {
      params.append('resource', auditFilters.resource.trim());
    }
    if (auditFilters.details.trim()) {
      params.append('details', auditFilters.details.trim());
    }
    if (auditFilters.ipAddress.trim()) {
      params.append('ipAddress', auditFilters.ipAddress.trim());
    }
    if (auditFilters.from) {
      params.append('from', auditFilters.from);
    }
    if (auditFilters.to) {
      params.append('to', auditFilters.to);
    }

    try {
      const data = await requestJson<unknown[]>(`/audit?${params.toString()}`, signal ? { signal } : {});
      setAuditLogs(Array.isArray(data) ? data.map(normalizeAuditLog) : []);
    } catch (error) {
      if (!isAbortError(error)) {
        setAuditError(getErrorMessage(error, 'Audit-Log konnte nicht geladen werden.'));
      }
    } finally {
      setAuditLoading(false);
    }
  }, [auditFilters, auditPage]);

  const resetAuditFilters = () => {
    setAuditFilters({
      search: '',
      userId: '',
      action: '',
      resource: '',
      details: '',
      ipAddress: '',
      timeRange: 'all',
      from: '',
      to: '',
    });
  };

  const auditActions = useMemo(
    () => Array.from(new Set(auditLogs.map((log) => log.action))).sort((a, b) => a.localeCompare(b)),
    [auditLogs],
  );

  const auditResources = useMemo(
    () => Array.from(new Set(auditLogs.map((log) => log.resource))).sort((a, b) => a.localeCompare(b)),
    [auditLogs],
  );

  const auditFiltered = useMemo(() => {
    const now = Date.now();
    const rangeStart: Record<AuditTimeRange, number | null> = {
      all: null,
      '1h': now - 60 * 60 * 1000,
      '24h': now - 24 * 60 * 60 * 1000,
      '7d': now - 7 * 24 * 60 * 60 * 1000,
      '30d': now - 30 * 24 * 60 * 60 * 1000,
    };
    const query = auditFilters.search.trim().toLowerCase();
    const userId = auditFilters.userId.trim().toLowerCase();
    const action = auditFilters.action.trim().toLowerCase();
    const resource = auditFilters.resource.trim().toLowerCase();
    const details = auditFilters.details.trim().toLowerCase();
    const ipAddress = auditFilters.ipAddress.trim().toLowerCase();
    const from = auditFilters.from ? new Date(`${auditFilters.from}T00:00:00`).getTime() : null;
    const to = auditFilters.to ? new Date(`${auditFilters.to}T23:59:59`).getTime() : null;

    return auditLogs.filter((log) => {
      const createdAt = new Date(log.createdAt).getTime();
      if (Number.isNaN(createdAt)) {
        return false;
      }
      const minimum = rangeStart[auditFilters.timeRange];
      if (minimum !== null && createdAt < minimum) {
        return false;
      }
      if (from !== null && createdAt < from) {
        return false;
      }
      if (to !== null && createdAt > to) {
        return false;
      }
      if (userId && log.userId.toLowerCase() !== userId && !(log.userName || '').toLowerCase().includes(userId)) {
        return false;
      }
      if (action && !log.action.toLowerCase().includes(action)) {
        return false;
      }
      if (resource && !log.resource.toLowerCase().includes(resource)) {
        return false;
      }
      if (ipAddress && !(log.ipAddress || '').toLowerCase().includes(ipAddress)) {
        return false;
      }
      const detailsText = `${log.oldData || ''} ${log.newData || ''} ${log.userAgent || ''}`.toLowerCase();
      if (details && !detailsText.includes(details)) {
        return false;
      }
      if (query) {
        const searchable = `${log.action} ${log.resource} ${log.userId} ${log.userName || ''} ${log.ipAddress || ''} ${detailsText}`.toLowerCase();
        if (!searchable.includes(query)) {
          return false;
        }
      }
      return true;
    });
  }, [auditLogs, auditFilters]);

  const auditActionOptions = useMemo(
    () => Array.from(new Set([...auditActions, ...(auditFilters.action ? [auditFilters.action] : [])])).map((value) => ({
      value,
      label: value,
    })),
    [auditActions, auditFilters.action],
  );

  const auditResourceOptions = useMemo(
    () => Array.from(new Set([...auditResources, ...(auditFilters.resource ? [auditFilters.resource] : [])])).map((value) => ({
      value,
      label: value,
    })),
    [auditResources, auditFilters.resource],
  );

  useEffect(() => {
    auditRequestRef.current?.abort();
    const controller = new AbortController();
    auditRequestRef.current = controller;
    void fetchAuditLogs(controller.signal);

    return () => {
      if (auditRequestRef.current === controller) {
        auditRequestRef.current = null;
      }
      controller.abort();
    };
  }, [fetchAuditLogs]);

  useEffect(() => {
    setAuditPage(1);
  }, [auditFilters]);

  useEffect(() => () => {
    auditRequestRef.current?.abort();
  }, []);

  const breadcrumbItems = [
    { label: 'Administration', href: '/admin' },
    { label: 'Sicherheit', href: '/admin/security' },
    { label: 'Audit-Log', current: true },
  ];

  return (
    <div className="space-y-6">
      <Breadcrumb items={breadcrumbItems} />

      <Card>
        <CardHeader className="mb-4">
          <CardTitle>Audit-Log</CardTitle>
          <CardDescription>
            Systemereignisse nach Zeit, Benutzer, Aktion, Bereich, Details und IP-Adresse durchsuchen.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {auditError && auditLogs.length === 0 ? (
            <ErrorState description={auditError} onRetry={() => void fetchAuditLogs()} />
          ) : auditError ? (
            <Alert variant="error" title="Audit-Log konnte nicht vollständig geladen werden" dismissible onDismiss={() => setAuditError(null)}>
              {auditError}
            </Alert>
          ) : null}

          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
            <Input
              aria-label="Audit-Einträge durchsuchen"
              placeholder="Freie Suche ..."
              value={auditFilters.search}
              onChange={(event) => setAuditFilters((current) => ({ ...current, search: event.target.value }))}
              leftIcon="Search"
            />
            <Select
              aria-label="Zeitraum filtern"
              value={auditFilters.timeRange}
              onChange={(event) => setAuditFilters((current) => ({ ...current, timeRange: event.target.value as AuditTimeRange }))}
              options={AUDIT_TIME_OPTIONS}
            />
            <Input
              aria-label="Startdatum"
              type="date"
              value={auditFilters.from}
              onChange={(event) => setAuditFilters((current) => ({ ...current, from: event.target.value }))}
            />
            <Input
              aria-label="Enddatum"
              type="date"
              value={auditFilters.to}
              onChange={(event) => setAuditFilters((current) => ({ ...current, to: event.target.value }))}
            />
            <Input
              aria-label="Benutzer filtern"
              placeholder="Benutzer-ID oder Name"
              value={auditFilters.userId}
              onChange={(event) => setAuditFilters((current) => ({ ...current, userId: event.target.value }))}
              leftIcon="User"
            />
            <Select
              aria-label="Aktion filtern"
              placeholder="Aktion auswählen"
              value={auditFilters.action}
              onChange={(event) => setAuditFilters((current) => ({ ...current, action: event.target.value }))}
              options={auditActionOptions}
            />
            <Select
              aria-label="Bereich filtern"
              placeholder="Bereich auswählen"
              value={auditFilters.resource}
              onChange={(event) => setAuditFilters((current) => ({ ...current, resource: event.target.value }))}
              options={auditResourceOptions}
            />
            <Input
              aria-label="IP-Adresse filtern"
              placeholder="IP-Adresse"
              value={auditFilters.ipAddress}
              onChange={(event) => setAuditFilters((current) => ({ ...current, ipAddress: event.target.value }))}
              leftIcon="Globe"
            />
            <div className="sm:col-span-2 lg:col-span-4">
              <Input
                aria-label="Details durchsuchen"
                placeholder="Details, alte oder neue Daten durchsuchen ..."
                value={auditFilters.details}
                onChange={(event) => setAuditFilters((current) => ({ ...current, details: event.target.value }))}
                leftIcon="FileSearch"
              />
            </div>
          </div>

          <div className="flex justify-end">
            <Button variant="ghost" size="sm" leftIcon="RotateCcw" onClick={resetAuditFilters}>
              Filter zurücksetzen
            </Button>
          </div>

          {auditLoading && auditLogs.length === 0 ? (
            <SkeletonTable rows={8} columns={6} />
          ) : auditFiltered.length === 0 ? (
            <EmptyState
              icon="History"
              title="Keine Audit-Einträge gefunden"
              description="Für die gewählten Filter sind keine Prüfprotokolle vorhanden."
              action={
                <Button variant="outline" size="sm" onClick={resetAuditFilters}>
                  Alle Filter zurücksetzen
                </Button>
              }
            />
          ) : (
            <Table
              columns={[
                {
                  key: 'createdAt',
                  header: 'Zeit',
                  className: 'w-40',
                  render: (log: AuditLog) => (
                    <span className="whitespace-nowrap font-mono text-xs text-neutral-700 dark:text-neutral-300">
                      {formatDate(log.createdAt, true)}
                    </span>
                  ),
                },
                {
                  key: 'user',
                  header: 'Benutzer',
                  className: 'min-w-[180px]',
                  render: (log: AuditLog) => (
                    <div>
                      <p className="truncate text-sm font-medium text-neutral-900 dark:text-white">{log.userName || '-'}</p>
                      <p className="truncate font-mono text-[11px] text-neutral-500 dark:text-neutral-400">{log.userId}</p>
                    </div>
                  ),
                },
                {
                  key: 'action',
                  header: 'Aktion',
                  className: 'min-w-[180px]',
                  render: (log: AuditLog) => {
                    const variant = /DENY|REMOVE|DELETE|FAILED|ERROR/i.test(log.action)
                      ? 'emergency'
                      : /GRANT|CREATE|UPDATE|SYNC|LOGIN/i.test(log.action)
                        ? 'medical'
                        : 'primary';
                    return <Badge variant={variant} size="sm">{log.action}</Badge>;
                  },
                },
                {
                  key: 'resource',
                  header: 'Bereich',
                  className: 'min-w-[160px]',
                  render: (log: AuditLog) => (
                    <span className="text-sm text-neutral-700 dark:text-neutral-300">{log.resource}</span>
                  ),
                },
                {
                  key: 'details',
                  header: 'Details',
                  className: 'min-w-[220px]',
                  render: (log: AuditLog) => <AuditDetails log={log} />,
                },
                {
                  key: 'ipAddress',
                  header: 'IP',
                  className: 'w-36',
                  render: (log: AuditLog) => (
                    <code className="whitespace-nowrap text-xs text-neutral-700 dark:text-neutral-300">{log.ipAddress || '-'}</code>
                  ),
                },
              ]}
              data={auditFiltered}
              keyExtractor={(log) => log.id}
              loading={auditLoading && auditLogs.length > 0}
              emptyMessage="Keine Audit-Einträge entsprechen den aktuellen Filtern."
            />
          )}

          {auditLogs.length > 0 && (
            <div className="flex flex-col items-center justify-between gap-3 border-t border-neutral-200 pt-4 dark:border-neutral-700 sm:flex-row">
              <span className="text-xs text-neutral-500 dark:text-neutral-400">
                Seite {auditPage} · {auditFiltered.length} angezeigte Einträge{auditLogs.length >= AUDIT_PAGE_SIZE ? ' · weitere Seite verfügbar' : ''}
              </span>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={auditPage === 1 || auditLoading}
                  onClick={() => setAuditPage((page) => Math.max(1, page - 1))}
                >
                  <icons.ChevronLeft className="h-4 w-4" />
                  Zurück
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={auditLoading || auditLogs.length < AUDIT_PAGE_SIZE}
                  onClick={() => setAuditPage((page) => page + 1)}
                >
                  Weiter
                  <icons.ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}